import { Series, SeriesOptionsType } from 'highcharts';
import { orderBy, uniqBy } from 'lodash';
import moment from 'moment';
import {
  Chart,
  ChartData,
  ChartDataResult,
  ChartTemplate,
  ChartType,
} from '../../api/graphql/generated';
import { ChartUiFields } from './configure-chart';

const getChartType = (data?: ChartData): 'column' | 'line' => {
  if (data) {
    const { chartType } = data.options;
    switch (chartType) {
      case ChartType.LINE:
        return 'line';
    }
  }
  return 'column';
};

const getEpochTimeOfPoint = (point: ChartDataResult): number => {
  return Number(moment(point.interval).format('x'));
};

const getPointInterval = (results?: ChartDataResult[]): number | undefined => {
  const filteredResults = uniqBy(results, 'interval');
  if (filteredResults && filteredResults.length > 1) {
    const d1 = getEpochTimeOfPoint(filteredResults[1]);
    const d0 = getEpochTimeOfPoint(filteredResults[0]);
    return d1 - d0;
  }
};

const getPointStart = (results?: ChartDataResult[]): number | undefined => {
  if (results) {
    const d0 = getEpochTimeOfPoint(results[0]);
    return d0;
  }
};

const getSeries = (
  sourceId: string,
  results?: ChartDataResult[],
  groupBy?: string,
  chartUiFields?: ChartUiFields
): (number | null)[] | undefined => {
  const series =
    results?.filter((r) =>
      r.sourceId === sourceId && r.groupBy ? r.groupBy === groupBy : true
    ) ?? [];

  return series.map((r) =>
    r.value === 0 && chartUiFields?.hideZeroValues === true ? null : r.value
  );
};

const getSeriesNames = (
  results?: ChartDataResult[]
): { sourceId: string; name: string; groupBy?: string }[] | undefined => {
  if (results) {
    const labels = results.map((r) => {
      const name = r.label + (r.groupBy ? ` - ${r.groupBy}` : '');
      return {
        sourceId: r.sourceId,
        name,
        groupBy: r.groupBy ?? undefined,
      };
    });

    return uniqBy(labels, 'name');
  }
};

const formatChartSeriesData = (
  data?: ChartData,
  chartUiFields?: ChartUiFields
): SeriesOptionsType[] => {
  const formattedChartData: SeriesOptionsType[] = [];
  if (data) {
    const orderedResults = orderBy(
      data.results,
      ['interval', 'label', 'groupBy'],
      ['asc']
    );

    getSeriesNames(orderedResults)?.forEach(({ sourceId, name, groupBy }) => {
      const type = getChartType(data);
      const filteredData = orderedResults.filter(
        (r) =>
          r.sourceId === sourceId && (groupBy ? r.groupBy === groupBy : true)
      );

      formattedChartData.push({
        type,
        name,
        id: sourceId + (groupBy ?? ''),
        pointStart: getPointStart(filteredData),
        pointInterval: getPointInterval(filteredData),
        data: getSeries(sourceId, filteredData, groupBy, chartUiFields),
        stack: sourceId,
      });
    });
  }
  return formattedChartData;
};

const upsertChartSeriesData = (
  chart?: Highcharts.Chart | null,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  series?: SeriesOptionsType & { data?: any },
  redraw = false
): void => {
  if (chart && series) {
    const existingSeries = chart.get(`${series.id}`) as Series;
    if (existingSeries) {
      existingSeries.update(series, redraw);
      existingSeries.setData(series.data, redraw);
    } else {
      chart.addSeries(series, redraw);
    }
  }
};

const orderCharts = (charts?: Chart[] | null): Chart[] => {
  return orderBy(
    charts,
    ['displayOrder', (chart) => chart.title.toLowerCase()],
    ['asc', 'asc']
  );
};

const orderChartTemplates = (
  templates?: ChartTemplate[] | null
): ChartTemplate[] => {
  return orderBy(templates, ['displayOrder', 'name'], ['asc', 'asc']);
};

const redrawChart = (chart?: Highcharts.Chart): void => {
  chart?.redraw();
};

const clearChartSeries = (chart?: Highcharts.Chart): void => {
  if (chart) {
    while (chart.series.length) {
      chart.series[0].remove();
    }
  }
};

export {
  getChartType,
  formatChartSeriesData,
  upsertChartSeriesData,
  orderCharts,
  orderChartTemplates,
  redrawChart,
  clearChartSeries,
};
